//* Import the from libraries
import React, { useState, useCallback, useEffect } from "react";

//* Import hooks from react-router-dom
import { useNavigate } from "react-router-dom";

//* Import the List component
import { List } from "../../../component/list/list";

//* Import the Loading component
import { Loading } from "../../../component/common/Loading";

//* Import the useAdmins hook
import { useAdmins } from "../../../hooks/admins/useAdmins";

//* Import the AuthAPI service
import { AuthAPI } from "../../../services/apis/AuthAPI";

//* Import the UserRole enum
import { UserRole } from "../../../constants/enum/UserRole";

//* Import the CSVAdminManagementView component
import { CSVAdminManagementView } from "../../UsersAdminView/list/CSVManegementView";

type Admin = {
  id: string;
  name: string;
  email: string;
  createdAt: string;
  updatedAt: string;
};

type User = {
  role: UserRole;
};

var debounce = require("lodash.debounce");

export function UsersAdminView() {
  const token = localStorage.getItem("token") || "";
  const navigate = useNavigate();

  const [me, setMe] = useState<User | null>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showCSVAdminModal, setShowCSVAdminModal] = useState(false);
  const [totalAdmins, setTotalAdmins] = useState(0);

  const {
    admins,
    loading,
    error,
    setSearch,
    pagination,
    setPagination,
    deleteAdmin,
    refetch,
    changePage,
    isDeleting,
  } = useAdmins(token);

  useEffect(() => {
    async function fetchMe() {
      try {
        const response = await AuthAPI.me(token);
        setMe(response.data);
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    }
    fetchMe();
  }, [token]);

  useEffect(() => {
    setTotalAdmins(pagination.totalItems || 0);
  }, [pagination.totalItems]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.trim().length >= 3) {
        setSearch(value);
      } else {
        setSearch("");
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
      setIsSearching(false);
    }, 500),
    [setSearch, setPagination, setIsSearching]
  );

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setSearchInputValue(value);
      setIsSearching(true);
      debouncedSearch(value);
    },
    [debouncedSearch, setSearchInputValue, setIsSearching]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (adminToDelete) {
      try {
        const currentPageItems = admins.length;
        const currentPage = pagination.currentPage;

        await deleteAdmin(adminToDelete);

        if (currentPageItems === 1 && currentPage > 1) {
          changePage(currentPage - 1);
        }

        setShowWarning(false);
        setAdminToDelete(null);
      } catch (error) {
        console.error("Error deleting admin:", error);
      }
    }
  }, [
    adminToDelete,
    deleteAdmin,
    admins.length,
    pagination.currentPage,
    changePage,
  ]);

  const handleDeleteActionClick = useCallback((id: string) => {
    setAdminToDelete(id);
    setShowWarning(true);
  }, []);

  const handleChangePagination = useCallback(
    (value: number) => {
      changePage(value);
    },
    [changePage]
  );

  const onImportClick = () => {
    setShowCSVAdminModal(true);
  };

  if (!me || isDeleting) {
    return <Loading />;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="row">
      <List
        search_input_value={searchInputValue}
        hideManager
        search_text="Pesquise pelo nome ou email do administrador"
        onChangeSearchInput={handleSearchInputChange}
        onSearchButtonClick={refetch}
        additionalButtons={
          me.role === UserRole.ADMIN_USER
            ? []
            : [
                {
                  label: "Importar administrador",
                  eventHandler: onImportClick,
                },
              ]
        }
        title={`Administradores (Total: ${totalAdmins})`}
        not_found_message="Nenhum administrador encontrado"
        button_link="/users-admin/create"
        button_text="Adicionar administrador"
        deleting_message="Ao deletar este administrador, ele não estará mais disponível no sistema"
        onDeleteCancel={() => setShowWarning(false)}
        onDeleteConfirm={handleDeleteConfirm}
        onDeleteActionClick={handleDeleteActionClick}
        onEditActionClick={(id) => navigate(`/users-admin/edit/${id}`)}
        onChangePagination={handleChangePagination}
        show_warning={showWarning}
        loading_screen={loading || isSearching}
        items={admins.map((admin: Admin) => ({
          ...admin,
          key: admin.id,
        }))}
        pagination={{
          last_page: pagination.totalPages,
          current_page: pagination.currentPage,
        }}
        fields={[
          { name: "name", type: "text", header: "Nome" },
          { name: "email", type: "text", header: "Email" },
          { name: "createdAt", type: "date", header: "Criado em" },
        ]}
        loading_search={isSearching}
      />
      {showCSVAdminModal && (
        <CSVAdminManagementView
          token={token}
          onClose={() => setShowCSVAdminModal(false)}
          refetch={refetch}
        />
      )}
    </div>
  );
}
